import React, { useEffect, useRef, useState } from "react";
import {
  Card,
  Typography,
  Grid,
  Box,
  Modal,
  IconButton,
  CircularProgress,
} from "@mui/material";
import FullscreenExit from "@mui/icons-material/FullscreenExit";
import Fullscreen from "@mui/icons-material/Fullscreen";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { makeStyles } from "tss-react/mui";
import Button from "../../components/Button";
import Renderer from "../../components/renderer";
import { useQuiz, useQuizTimer } from "./hooks";
import {
  FORCE_ENABLE_TEXT_TO_SPEECH,
  getDisplayTypeWithContent,
  slugify,
  formatTime,
} from "../../utils";
import Header from "../../components/question/Header";
import AnswerList from "../../components/question/AnswerList";
import GroupDetails from "../../components/quiz/GroupDetails";
import MatchTheFollowing from "../../components/quiz/MatchTheFollowing";
import SpeechEvaluation from "../../components/quiz/SpeechEvaluation";
import { borderRadius, padding } from "@mui/system";
import { ButtonYellow } from "../../components/buttons";
import {
  queueAssessmentForEvaluation,
  saveAssessmentAnswersActivity,
} from "../../store/quiz/quizBaseApi";
import cloclImage from "../../assets/images/quiz/clock.svg";

const useStyles = makeStyles()((theme) => ({
  actionButton: {
    width: "200px!important",
  },
  clockContainer: {
    display: "flex",
    alignItems: "center",
    color: "#fff", // White text color
  },
  clockIcon: {
    width: "50px", // Adjust the size of the clock image
    height: "50px",
    backgroundImage: `url(${cloclImage})`, // Add your clock image path here
    backgroundSize: "contain",
    backgroundRepeat: "no-repeat",
    zIndex: 1,
  },
  timeText: {
    fontSize: "1em", // Adjust the size to match the large font
    marginLeft: theme.spacing(-2),
    borderRadius: "14px",
    backgroundColor: "#FF5722",
    padding: theme.spacing(1),
    fontWeight: "bold",
    lineHeight: 0.5,
    width: "70px",
    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
    textAlign: "center",
    textShadow: "0px 0px 2px rgba(0, 0, 0, 0.25)",
  },
}));

export default function QuestionScreen({
  testType,
  style = {},
  setAssessmentEnded,
}) {
  const { classes } = useStyles();
  const [activeQuestion, setActiveQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState({});
  const [endQuiz, setEndQuiz] = useState(false);
  const [postingResults, setPostingResults] = useState(false);

  const {
    questions,
    quizDetails,
    result,
    setResult,
    currentQuestionGroup,
    setCurrentQuestionGroup,
    groupQuestionCount,
    setGroupQuestionCount,
    questionGroups,
    setCurrentQuestionNumber,
    currentCourseDetails,
    setCurrentCourseDetails,
    setEvaluationId,
    setEvaluationResult,
  } = useQuiz();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const quizContainerRef = useRef();

  const currentQuestion = questions[activeQuestion];

  const { questionDetails, type, answerDetails, groupDetails } =
    currentQuestion;
  const options = answerDetails.options || {};
  const choices = options.values || [];
  const helpText = options.helpText || {};
  const correctAnswers =
    [questionDetails.options && questionDetails.options.answerKey] || [];
  // const { MTFLHS, MTFRHS, relation } = options;
  const { MTFLHS, MTFRHS, values } = options;
  const correctAnswerCount =
    type === "MSQ" ? options.answerLabels?.length || 2 : 0;

  const headers = {
    FIB: "Read and Fill in the Blanks",
    MSQ: "Read and Select all correct Answer",
    MCQ: "Read and Select the correct Answer",
    MTF: "Match the Following",
  };

  const handleAnswerSelection = (e) => {
    const { checked, id } = e.target;
    if (!quizDetails?.preview) {
      if (type === "MSQ") {
        let data = JSON.parse(e.target.getAttribute("data") || "{}");

        setSelectedAnswer((prevSelectedAnswer) => {
          if (prevSelectedAnswer[id]) {
            // Removing selection
            const newSelectedAnswer = { ...prevSelectedAnswer };
            delete newSelectedAnswer[id];
            return newSelectedAnswer;
          } else {
            // Adding selection
            const currentSelections = Object.keys(prevSelectedAnswer);
            const newSelectedAnswer = { ...prevSelectedAnswer };

            if (currentSelections.length >= correctAnswerCount) {
              // Remove the first selected item (FIFO)
              const firstSelectedKey = currentSelections[0];
              delete newSelectedAnswer[firstSelectedKey];
            }

            // Add the new selection
            newSelectedAnswer[id] = [data];
            return newSelectedAnswer;
          }
        });
      } else if (type === "MCQ" || type === "boolean") {
        let data = JSON.parse(e.target.getAttribute("data") || "{}");
        if (checked) {
          setSelectedAnswer({ [id]: data });
        }
      } else if (type === "FIB") {
        let number = questionDetails.text
            ?.split(/\{(\d+)}/g)
            .filter((e) => !isNaN(e)).length,
          value = e.target;
        if (!number.length) {
          number = 0;
        }
        if (value.length >= number) {
          setSelectedAnswer((prev) => ({
            ...prev,
            [slugify(questionDetails.text)]: value,
          }));
        }
      } else if (type === "MTF") {
        let data = e.target;
        setSelectedAnswer((prev) => ({
          ...prev,
          [slugify(questionDetails.text)]: data,
        }));
      }
    }
  };

  const handleQuizEndingProcess = async (_result) => {
    setPostingResults(true);

    // Clear the previous attempt's evaluation before requesting a new one.
    // evaluationId/evaluationResult persist in redux across attempts, so a
    // retake whose /evaluate request fails or lags would otherwise poll the
    // PREVIOUS attempt's already-evaluated row and show its (stale) score on
    // the result page. Nulling evaluationId also makes the Evaluating screen's
    // poll wait for the fresh id instead of settling on the old row.
    setEvaluationId(null);
    setEvaluationResult({});

    try {
      const { data } = await queueAssessmentForEvaluation({
        body: {
          ...currentCourseDetails,
          assessmentAnswers: _result,
        },
      });

      // Only advance to the Evaluating screen once we actually have a fresh
      // evaluationId for THIS attempt. Without it the poll can't identify the
      // new attempt, so navigating would risk showing a stale result.
      if (!data || !data.evaluationId) {
        throw new Error("evaluate response missing evaluationId");
      }
      setEvaluationId(data.evaluationId);
      localStorage.removeItem(
        `assessment-${currentCourseDetails.assessmentId}`,
      );

      // For the diagnostic test, also wipe the server-side saved answers
      // (ir_assessment_answers_activity, keyed by username+assessmentId) so a
      // retake after logging back in doesn't prefetch the previous attempt.
      // The course flow clears these via resetCourseProgress; the diagnostic
      // has no reset, so we blank them here on submit. The evaluation itself
      // uses the answers in the request body above, not this record, so this
      // is safe.
      if (testType === "diagnostic-test-v2" && currentCourseDetails.assessmentId) {
        try {
          await saveAssessmentAnswersActivity(
            currentCourseDetails.assessmentId,
            [],
          );
        } catch (clearErr) {
          console.error("error clearing diagnostic saved answers", clearErr);
        }
      }

      setEndQuiz(true);
    } catch (e) {
      console.error("error while posting evaluation", e);
      // Submit failed — don't navigate to the result flow with a stale/empty
      // evaluationId. Re-enable the submit control so the student can retry.
      setPostingResults(false);
    }
  };

  const onClickNext = () => {
    const isMatch =
      selectedAnswer.length === correctAnswers.length &&
      selectedAnswer.every((answer) => correctAnswers.includes(answer));

    const existingIndex = result.findIndex(
      (res) => res.id === currentQuestion.id,
    );

    let updatedResult = [...result];

    if (existingIndex !== -1) {
      // If the question already exists, update its selected answer and match status
      updatedResult[existingIndex] = {
        ...currentQuestion,
        selectedAnswer,
        isMatch,
      };
    } else {
      // If not found, add a new entry
      updatedResult.push({
        ...currentQuestion,
        selectedAnswer,
        isMatch,
      });
    }

    // adding selected answer, and if answer matches key to result array with current question
    // setResult([...result, { ...currentQuestion, selectedAnswer, isMatch }])
    setResult(updatedResult);

    if (currentCourseDetails.assessmentId) {
      let cached =
        JSON.parse(
          localStorage.getItem(
            `assessment-${currentCourseDetails.assessmentId}`,
          ),
        ) || [];
      let idx = cached.findIndex((e) => e.id === currentQuestion.id);
      if (idx !== -1) {
        cached[idx] = {
          ...currentQuestion,
          selectedAnswer,
          isMatch,
        };
      } else {
        cached.push({
          ...currentQuestion,
          selectedAnswer,
          isMatch,
        });
      }
      saveAssessmentAnswersActivity(currentCourseDetails.assessmentId, cached);
      localStorage.setItem(
        `assessment-${currentCourseDetails.assessmentId}`,
        JSON.stringify(cached),
      );
    }

    if (activeQuestion !== questions.length - 1) {
      setActiveQuestion((prev) => prev + 1);

      const nextIndex = activeQuestion + 1;
      const nextQuestion = questions[nextIndex];
      const nextResult = updatedResult.find(
        (res) => res.id === nextQuestion.id,
      );
      setSelectedAnswer(nextResult?.selectedAnswer || {});
    } else {
      handleQuizEndingProcess(updatedResult);
    }

    // setSelectedAnswer({});

    if (groupQuestionCount < currentQuestionGroup?.noOfQuestions) {
      setGroupQuestionCount(groupQuestionCount + 1);
    } else {
      let _question = questions[activeQuestion + 1];

      if (_question) {
        let groupId = _question.groupDetails?.id;

        if (groupId && questionGroups.has(groupId)) {
          setGroupQuestionCount(1);
          setCurrentQuestionGroup(questionGroups.get(groupId));
        }
      }
    }

    if (type == "MTF") {
      document.querySelectorAll(".line").forEach((e) => e.remove());
      document.querySelectorAll(".answer-item").forEach((e) => {
        e.style.backgroundColor = "#fff";
        e.removeAttribute("matched");
        e.removeAttribute("selected");
      });
    }
  };

  const onClickPrev = () => {
    if (activeQuestion > 0) {
      setActiveQuestion((prev) => prev - 1);

      const isMatch =
        selectedAnswer.length === correctAnswers.length &&
        selectedAnswer.every((answer) => correctAnswers.includes(answer));

      const existingIndex = result.findIndex(
        (res) => res.id === currentQuestion.id,
      );
      let updatedResult = [...result];

      if (existingIndex !== -1) {
        updatedResult[existingIndex] = {
          ...currentQuestion,
          selectedAnswer,
          isMatch,
        };
      } else {
        updatedResult.push({
          ...currentQuestion,
          selectedAnswer,
          isMatch,
        });
      }

      setResult(updatedResult);

      const prevIndex = activeQuestion - 1;
      const prevQuestion = questions[prevIndex];
      setActiveQuestion(prevIndex);

      // Retrieve previous question's selected answer from the result object
      const prevResult = result.find(
        (res) => res.id === questions[activeQuestion - 1]?.id,
      );

      if (prevResult?.selectedAnswer) {
        setSelectedAnswer(prevResult.selectedAnswer);

        if (prevQuestion.type === "MTF") {
          setTimeout(() => {
            Object.entries(prevResult.selectedAnswer).forEach(([lhs, rhs]) => {
              const leftItem = document.querySelector(`[data-lhs="${lhs}"]`);
              const rightItem = document.querySelector(`[data-rhs="${rhs}"]`);

              if (leftItem && rightItem) {
                leftItem.setAttribute("matched", true);
                rightItem.setAttribute("matched", true);
                leftItem.style.backgroundColor = "#d3f9d8";
                rightItem.style.backgroundColor = "#d3f9d8";

                // Redraw connecting lines if needed
                drawMatchingLine(leftItem, rightItem);
              }
            });
          }, 100);
        }
      } else {
        setSelectedAnswer({});
      }
    }
  };

  const onClickPreview = (type) => {
    if (type === "next") {
      setActiveQuestion((prev) => prev + 1);
    } else {
      setActiveQuestion((prev) => prev - 1);
    }
  };

  const goFullscreen = () => {
    const element = quizContainerRef.current;
    if (!element) return;

    if (element.requestFullscreen) {
      element.requestFullscreen();
    } else if (element.mozRequestFullscreen) {
      element.mozRequestFullscreen(); // Firefox
    } else if (element.webkitRequestFullscreen) {
      element.webkitRequestFullscreen(); // Safari
    } else if (element.msRequestFullscreen) {
      element.msRequestFullscreen(); // IE11
    }

    setIsFullscreen(true);
  };

  const exitFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const handleFullscreenToggle = () => {
    if (isFullscreen) {
      exitFullscreen();
    } else {
      goFullscreen();
    }
  };

  useEffect(() => {
    setCurrentQuestionNumber(activeQuestion);
  }, [activeQuestion]);

  useEffect(() => {
    const cached =
      JSON.parse(
        localStorage.getItem(`assessment-${currentCourseDetails.assessmentId}`),
      ) || [];
    if (cached?.length) {
      if (currentQuestion) {
        let answer = cached.find((el) => el.id == currentQuestion.id);
        // console.log(answer)
        if (answer) {
          setSelectedAnswer(answer.selectedAnswer);
        }
      }
    }
  }, [currentQuestion]);

  useEffect(() => {
    goFullscreen();

    if (currentQuestionGroup && !Object.keys(currentQuestionGroup).length) {
      let group = questionGroups.get(currentQuestion.groupDetails?.id);
      group && setCurrentQuestionGroup(group);
    }
    // eslint-disable-next-line
  }, []);

  return (
    <div ref={quizContainerRef}>
      <Card
        style={{
          ...styles.container,
          ...(isFullscreen ? { height: "calc(100vh - 88px)" } : {}),
        }}
      >
        {isFullscreen && <AssessmentTimer />}
        {headers[type] && (
          <Grid
            xs={12}
            gap={2}
            textAlign={"center"}
            display={"flex"}
            justifyContent={"space-between"}
            mt={isFullscreen ? 4 : 1}
          >
            <Grid item xs={11}>
              <Typography
                sx={{
                  mb: 1,
                  fontWeight: 700,
                  fontSize: {
                    xs: "1em",
                    sm: "1em",
                    md: "1em",
                    lg: "1.2em",
                    xl: "1.2em",
                  },
                }}
                variant="h5"
              >
                &nbsp;
              </Typography>
            </Grid>
            <Grid xs={1}>
              <IconButton
                onClick={handleFullscreenToggle}
                title={(isFullscreen ? "Exit" : "Enter") + " fullscreen mode"}
              >
                {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
              </IconButton>
            </Grid>
          </Grid>
        )}
        <div style={{ ...styles.quizContainer, ...style }}>
          <Typography
            sx={{
              mb: 1,
              textAlign: "center",
              fontWeight: 700,
              fontSize: {
                xs: "1em",
                sm: "1em",
                md: "1em",
                lg: "1.2em",
                xl: "1.2em",
              },
            }}
            variant="h5"
          >
            {headers[type]}
          </Typography>
          <Box
            sx={{
              height: "calc(100vh - 280px)",
              maxWidth: "100%",
              overflowX: "hidden",
              overflowY: "scroll",
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#F5F5F5",
                borderRadius: "10px",
              },
              "&:hover, &:focus-within": {
                "&::-webkit-scrollbar-thumb": {
                  background:
                    "linear-gradient(193.28deg, #29ABE2 5.42%, #2DA2E2 12.18%, #398CE2 33.81%, #417EE2 54.77%, #447AE2 73.02%)",
                  width: "12px",
                  cursor: "pointer",
                },
              },
            }}
          >
            <Header
              activeQuestion={activeQuestion}
              noOfQuestions={quizDetails.noOfQuestions}
              quiz={quizDetails}
              ended={endQuiz}
              testType={testType}
              setAssessmentEnded={setAssessmentEnded}
            />
            {groupDetails && Object.keys(groupDetails).length > 0 && (
              <GroupDetails style={styles.groupDetails} group={groupDetails} />
            )}
            {type !== "SE" && (
              <Renderer
                // content={questionDetails.text}
                // style={{ width: '100%', justifyContent: 'center', color: 'var(--mui-palette-primary-questionHead)', fontWeight: '600', fontSize: '1em' }}
                // mediaStyle={{ height: '150px', width: 'auto' }}
                // contentUrl={questionDetails.contentURL}
                // contentType={questionDetails.style}
                // overrideHTML={questionDetails.overrideHTML}
                // helpText={helpText}
                // handleAnswerSelection={handleAnswerSelection}
                // selected={selectedAnswer}
                // enableTextToAudio={questionDetails.enableTextToAudio || FORCE_ENABLE_TEXT_TO_SPEECH}
                // contentDisplayType={type}
                // templateType={questionDetails.displayTemplate}
                content={questionDetails.text}
                style={{
                  width: "100%",
                  justifyContent: "center",
                  color: "var(--mui-palette-primary-questionHead)",
                  fontWeight: "600",
                  fontSize: "1.3em",
                }}
                mediaStyle={{ height: "150px", width: "auto" }}
                contentUrl={questionDetails.contentURL}
                contentType={questionDetails.style}
                overrideHTML={questionDetails.overrideHTML}
                // helpText={helpText}
                helpText={
                  helpText?.text || helpText?.contentURL ? helpText : ""
                }
                handleAnswerSelection={handleAnswerSelection}
                selected={type !== "FIB" ? selectedAnswer : undefined} // Don't pass selected for FIB
                value={type === "FIB" ? selectedAnswer : undefined} // Pass value for FIB
                enableTextToAudio={
                  questionDetails.enableTextToAudio ||
                  FORCE_ENABLE_TEXT_TO_SPEECH
                }
                contentDisplayType={type}
                templateType={questionDetails.displayTemplate}
              />
            )}
            {type === "MTF" ? (
              <MatchTheFollowing
                selectedAnswer={selectedAnswer}
                questionId={currentQuestion.id}
                relation={values[0]?.label}
                left={MTFLHS}
                right={MTFRHS}
                evaluated={false}
                handleAnswerSelection={handleAnswerSelection}
                enableTextToAudio={true}
                quizPart={true}
                question={slugify(questionDetails?.text)}
              />
            ) : type === "SE" ? (
              <SpeechEvaluation
                showTitle={true}
                showText={true}
                title={
                  "Read aloud the following sentence and then we will evaluate your speech:"
                }
                question={questionDetails}
                sampleRate={16000}
              />
            ) : (
              <AnswerList
                content={getDisplayTypeWithContent(currentQuestion)}
                choices={choices}
                selectedAnswer={selectedAnswer}
                type={type}
                handleAnswerSelection={handleAnswerSelection}
                question={questionDetails}
              />
            )}
          </Box>
        </div>
      </Card>

      {!quizDetails.preview ? (
        <Card
          sx={{
            px: 4,
            py: 2,
            width: " -webkit-fill-available",
            // bottom: "-15px",
            // zIndex: 4
          }}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "#F6F7FB",
          }}
        >
          <ButtonYellow
            onClick={onClickPrev}
            className={classes.actionButton}
            buttonProps={{ disabled: activeQuestion === 0 }}
          >
            <Typography
              sx={{
                fontWeight: "700",
                fontFamily: '"Quicksand", sans-serif',
                fontSize: { xs: "1em", sm: "1em", md: "1.2em", lg: "1.4em" },
                zIndex: 2,
              }}
            >
              Prev
            </Typography>
          </ButtonYellow>
          <ButtonYellow
            onClick={onClickNext}
            buttonProps={{ disabled: Object.keys(selectedAnswer).length === 0 }}
            className={classes.actionButton}
          >
            <Typography
              sx={{
                fontWeight: "700",
                fontFamily: '"Quicksand", sans-serif',
                fontSize: { xs: "1em", sm: "1em", md: "1.2em", lg: "1.4em" },
                zIndex: 2,
              }}
            >
              {/* Next  */}
              {activeQuestion === questions.length - 1 ? "Submit" : "Next"}
            </Typography>
          </ButtonYellow>
        </Card>
      ) : (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Button
            onClick={() => onClickPreview("previous")}
            style={{ marginRight: "1rem" }}
            variant="contained"
            size="lg"
            placeholder="Previous"
            disabled={activeQuestion === 0}
            icon={<NavigateNextIcon />}
            sx={{
              minWidth: "100px",
              p: 1,
            }}
          />
          <Button
            onClick={() => onClickPreview("next")}
            icon={postingResults ? <CircularProgress /> : <NavigateNextIcon />}
            variant="contained"
            size="lg"
            placeholder="Next"
            disabled={activeQuestion === questions.length - 1 || postingResults}
            sx={{
              minWidth: "100px",
              background: "var(--mui-palette-primary-button)",
              p: 1,
            }}
          />
        </div>
      )}
    </div>
  );
}

function AssessmentTimer() {
  const { classes } = useStyles();
  const { timer } = useQuizTimer();

  return (
    <Grid xs={12} display={"flex"} justifyContent={"center"} sx={{ mt: 4 }}>
      <Box className={classes.clockContainer}>
        <Box className={classes.clockIcon} />
        <Typography className={classes.timeText}>
          {formatTime(timer)}
        </Typography>
      </Box>
    </Grid>
  );
}

const styles = {
  container: {
    border: "none",
    padding: "0px",
    boxShadow: "none",
  },
  groupDetails: {
    marginTop: "4rem",
    marginBottom: "2rem",
    background: "var(--mui-palette-background-level3)",
    padding: "20px",
    borderRadius: "8px",
  },
  quizContainer: {
    height: "calc(100vh - 300px)",
    // background: 'yellow',
    borderRadius: "4px",
    padding: "0",
    // marginBottom: '70px',
    // position: 'relative',
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    // height: '100%',
  },
};
